import { useQuery, keepPreviousData } from '@tanstack/react-query';

import { leadAssignmentService } from '../services/service';

/**
 * Per-agent pipeline-stage counts (system + custom stages) for the Status
 * Overview tab. Agents run their own custom pipelines now, so the overview
 * shows each agent's assigned leads broken down by THEIR stages instead of
 * one meaningless agency-wide row.
 */
/**
 * `poll` exists because this query is not cheap — measured at ~807ms on a large agency
 * (Duse: recursive agency tree + a group-by across every assigned lead).
 *
 * The Status Overview tab wants it live, so it keeps the 60s refetch. The lead-assignment
 * filter only needs the LIST of stages people use, which changes maybe once a week — so it
 * opts out of polling and reads from cache. Without this the filter would have added a
 * fresh 807ms query every 60 seconds for every owner sitting on the Unassigned tab, where
 * nothing was previously calling this endpoint at all (the two components that do live on
 * a different tab, so React Query never dedupes them together).
 */
export const useAgentStatusBreakdown = (enabled = true, poll = true) => {
  return useQuery<{
    agents: Record<string, Record<string, number>>;
    agentNames: Record<string, string>;
    customStages: { name: string; owners: string[]; assignedCount: number }[];
  }>({
    queryKey: ['agent-status-breakdown'],
    queryFn: () => leadAssignmentService.getAgentStatusBreakdown(),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: poll ? 30 * 1000 : 10 * 60 * 1000,
    refetchInterval: poll ? 60 * 1000 : false,
    refetchOnWindowFocus: poll,
  });
};
